import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Terms of Service | The Digital Debrief" },
      {
        name: "description",
        content:
          "Terms of Service for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
      { property: "og:title", content: "Terms of Service | The Digital Debrief" },
      {
        property: "og:description",
        content:
          "Terms of Service for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/terms" },
      { name: "twitter:title", content: "Terms of Service | The Digital Debrief" },
      {
        name: "twitter:description",
        content:
          "Terms of Service for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 py-24 lg:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
        The Digital Debrief
      </p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">Terms of Service</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
      </p>

      <div className="mt-10 space-y-8 text-base leading-relaxed">
        <section>
          <h2 className="font-display text-2xl">1. Agreement to Terms</h2>
          <p>
            These Terms of Service ("Terms") form a binding agreement between you and Scott
            Cunningham ("Scott," "we," "us," or "our") and govern your access to and use of{" "}
            <strong>The Digital Debrief</strong> (the "Newsletter"), the website at
            scottcbusiness.com (the "Site"), and any related content, social channels, or
            communications (collectively, the "Services"). By subscribing, opening an email, or
            otherwise using the Services, you agree to these Terms. If you do not agree, do not
            subscribe or use the Services.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">2. Eligibility</h2>
          <p>
            You must be at least 16 years old (or the age of digital consent in your jurisdiction)
            to subscribe. By subscribing, you represent that you meet this requirement and that the
            information you provide is accurate.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">3. The Newsletter</h2>
          <p>
            The Newsletter delivers commentary, analysis, links, and curated content related to
            social media, content strategy, AI, marketing, and related topics. The Newsletter is
            currently
            <strong> offered free of charge</strong>; we do not collect payment, run a paywall, or
            operate paid tiers. We may, at our discretion, change frequency, format, topics, or
            pause or discontinue the Newsletter at any time without notice.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">4. Email Delivery via beehiiv</h2>
          <p>
            The Newsletter is sent through <strong>beehiiv, Inc.</strong> ("beehiiv"), our email
            service provider. By subscribing, you also become subject to beehiiv's{" "}
            <a
              href="https://www.beehiiv.com/tou"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Use
            </a>{" "}
            and{" "}
            <a
              href="https://www.beehiiv.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
            . beehiiv processes your email and engagement data on our behalf to deliver the
            Newsletter.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">5. Subscriptions and Unsubscribing</h2>
          <p>
            You may subscribe by submitting a valid email address. You may unsubscribe at any time
            using the unsubscribe link included in every email or by contacting us. We reserve the
            right to remove, suspend, or block any subscriber at our sole discretion, including for
            abuse, unlawful activity, repeated bounces, or use that violates these Terms.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            <li>Use the Services for any unlawful, harmful, infringing, or fraudulent purpose;</li>
            <li>
              Subscribe using an email address you do not own or are not authorized to use, or use
              automated tools to subscribe in bulk;
            </li>
            <li>
              Republish, resell, redistribute, scrape, or systematically copy Newsletter content
              without written permission;
            </li>
            <li>
              Attempt to interfere with, disrupt, reverse engineer, or gain unauthorized access to
              the Services or to beehiiv's systems;
            </li>
            <li>
              Use the Services to train or fine-tune machine learning models, datasets, or AI
              systems without our prior written consent.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl">7. Intellectual Property</h2>
          <p>
            All content in the Newsletter and on the Site, including text, design, logos, graphics,
            and compilations, is owned by Scott Cunningham or licensed to us, and is protected by
            Canadian and international copyright, trademark, and other laws. Subject to these Terms,
            you are granted a limited, revocable, non-exclusive, non-transferable license to access
            and read the Newsletter for personal, non-commercial use. You may share individual
            issues or excerpts with clear attribution and a link back to the source.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">8. Third-Party Links and Services</h2>
          <p>
            The Newsletter and Site may link to or embed third-party websites, products, tools, or
            services. We do not control and are not responsible for those third parties, their
            content, or their practices. Your use of any third-party service is at your own risk and
            subject to that party's terms.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">9. No Professional Advice</h2>
          <p>
            Content is provided for general informational and editorial purposes only. It is not
            professional, legal, financial, tax, medical, or investment advice and should not be
            relied on as such. You are solely responsible for any decisions you make based on the
            content.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">10. Disclaimer of Warranties</h2>
          <p>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OR ERROR-FREE
            OPERATION. WE DO NOT WARRANT THAT THE SERVICES WILL MEET YOUR REQUIREMENTS OR THAT ANY
            DEFECTS WILL BE CORRECTED.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">11. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL SCOTT CUNNINGHAM BE LIABLE FOR
            ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR
            ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH
            THE SERVICES, WHETHER BASED IN CONTRACT, TORT, OR ANY OTHER LEGAL THEORY. OUR TOTAL
            AGGREGATE LIABILITY FOR ANY CLAIM RELATED TO THE SERVICES WILL NOT EXCEED ONE HUNDRED
            CANADIAN DOLLARS (CAD $100).
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Scott Cunningham from and against any claims,
            liabilities, damages, losses, and expenses (including reasonable legal fees) arising
            from (a) your use of the Services, (b) your breach of these Terms, or (c) your violation
            of any law or third-party right.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">13. Termination</h2>
          <p>
            We may suspend or terminate your access to the Services at any time, with or without
            notice or cause. Sections that by their nature should survive termination will survive,
            including intellectual property, disclaimers, limitation of liability, and
            indemnification.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">14. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. The "Last updated" date above reflects the
            most recent revision. Material changes will be communicated via the Newsletter or posted
            on this page. Your continued use of the Services after changes take effect constitutes
            acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">15. Governing Law and Jurisdiction</h2>
          <p>
            These Terms are governed by the laws of the Province of Ontario and the federal laws of
            Canada applicable therein, without regard to conflict-of-laws rules. You agree that the
            courts located in Toronto, Ontario will have exclusive jurisdiction over any dispute
            arising from or relating to these Terms or the Services.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">16. Severability and Entire Agreement</h2>
          <p>
            If any provision of these Terms is held unenforceable, the remaining provisions will
            remain in full force and effect. These Terms, together with our{" "}
            <a href="/privacy" className="underline">
              Privacy Policy
            </a>
            , constitute the entire agreement between you and us regarding the Services.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">17. Contact</h2>
          <p>
            Questions about these Terms? Email{" "}
            <a href="mailto:scottcbusiness@protonmail.com" className="underline">
              scottcbusiness@protonmail.com
            </a>
            .
          </p>
        </section>
      </div>
    </article>
  );
}
